import { Box, Button, useTheme } from '@mui/material';
import DOMPurify from 'dompurify';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useIsMobile } from '../../theme';
import { useEffect } from 'react';

interface Subject {
  title: string;
  document: string;
}

interface InstructionDocumentsProps {
  subjects: Subject[];
  currentIndex: number;
  onNavigate: (index: number) => void;
}

export default function InstructionDocuments({
  subjects,
  currentIndex,
  onNavigate,
}: InstructionDocumentsProps) {
  const currentSubject = subjects[currentIndex];
  const hasNext = currentIndex < subjects.length - 1;
  const hasPrev = currentIndex > 0;
  const theme = useTheme();
  const isMobile = useIsMobile();

  // Use a more type-safe approach without DOMPurify attribute hooks
  // Just allow basic attributes through DOMPurify config
  const purifyConfig = {
    ADD_ATTR: ['target', 'class', 'rel'], // Allow these attributes
    ALLOW_DATA_ATTR: false,
  };

  // Add click handler for external links
  useEffect(() => {
    const handleExternalLinks = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a.external-link') as HTMLAnchorElement | null;

      if (link) {
        e.preventDefault();
        // Open link in new window with specific parameters
        window.open(
          link.href,
          'newwindow',
          'width=1200,height=800,toolbar=yes,menubar=yes,scrollbars=yes,resizable=yes,location=yes,status=yes'
        );
      }
    };

    // Add the event listener to the document
    document.addEventListener('click', handleExternalLinks);

    // Clean up the event listener when component unmounts
    return () => {
      document.removeEventListener('click', handleExternalLinks);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flex: 1,
        position: 'relative',
        overflow: 'hidden', // Prevent outer container from scrolling
      }}
    >
      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto', // Only this content should scroll
          px: isMobile ? 2 : 4, // Horizontal padding
          pt: 3, // Top padding
          pb: 10, // Bottom padding to ensure content isn't hidden behind navigation
          backgroundColor: 'background.default', // Use theme background color
          color: 'text.primary', // Use theme text color
        }}
      >
        {currentSubject && (
          <Box
            className="instruction-content"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(currentSubject.document, purifyConfig),
            }}
            sx={{
              width: '100%',
              // Add styling for content elements with theme-aware colors
              '& h2': {
                ...theme.typography.h2,
                color: 'text.primary',
                mb: 2,
                mt: 0,
              },
              '& h3': {
                ...theme.typography.h3,
                color: 'text.primary',
                mb: 2,
                mt: 3,
              },
              '& p': {
                ...theme.typography.body1,
                color: 'text.primary',
                mb: 2,
              },
              '& ul, & ol': {
                mb: 2,
                pl: 3,
              },
              '& li': {
                ...theme.typography.body1,
                color: 'text.primary',
                mb: 1,
              },
              '& .important-note': {
                p: 2,
                borderLeft: `4px solid ${theme.palette.warning.main}`,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.05)',
                borderRadius: '0 4px 4px 0',
                my: 2,
              },
              '& a.external-link': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
              },
            }}
          />
        )}
      </Box>

      {/* Fixed Navigation Footer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          padding: isMobile ? 1 : 2,
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
          zIndex: 1,
          boxShadow: theme.shadows[1],
        }}
      >
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={() => onNavigate(currentIndex - 1)}
          disabled={!hasPrev}
          size={isMobile ? 'medium' : 'large'}
          sx={{
            color: theme.palette.text.primary,
            minWidth: 0,
            px: isMobile ? 1 : 2,
            '& .MuiButton-startIcon': {
              mr: isMobile ? 0.5 : 1,
            },
            '& .MuiButton-endIcon': {
              ml: isMobile ? 0.5 : 1,
            },
            typography: isMobile ? 'button' : 'h6',
          }}
        >
          {isMobile ? 'Prev' : 'Previous'}
        </Button>
        <Button
          variant="outlined"
          endIcon={<NavigateNextIcon />}
          onClick={() => onNavigate(currentIndex + 1)}
          disabled={!hasNext}
          size={isMobile ? 'medium' : 'large'}
          sx={{
            color: theme.palette.text.primary,
            minWidth: 0,
            px: isMobile ? 1 : 2,
            '& .MuiButton-startIcon': {
              mr: isMobile ? 0.5 : 1,
            },
            '& .MuiButton-endIcon': {
              ml: isMobile ? 0.5 : 1,
            },
            typography: isMobile ? 'button' : 'h6',
          }}
        >
          {isMobile ? 'Next' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}
